const normalizeText = (value) => String(value || '').toLowerCase();

const includesSearch = (target, search) => normalizeText(target).includes(normalizeText(search));

export const compareBy = (sortBy, sortDir = 'desc') => {
  const direction = sortDir === 'asc' ? 1 : -1;
  return (left, right) => {
    const leftValue = left?.[sortBy];
    const rightValue = right?.[sortBy];

    if (sortBy === 'key') {
      return normalizeText(leftValue).localeCompare(normalizeText(rightValue)) * direction;
    }

    const leftNum = Number(leftValue || 0);
    const rightNum = Number(rightValue || 0);
    if (leftNum === rightNum) return 0;
    return leftNum > rightNum ? direction : -direction;
  };
};

const groupMatchesFilters = (group, filters) => {
  const statusFilter = normalizeText(filters.status);
  const searchFilter = normalizeText(filters.search);

  const statusMatch = !statusFilter || Object.keys(group.statuses || {}).some((status) => normalizeText(status) === statusFilter);

  const haystack = [
    group.key,
    ...(group.clients || []),
    ...(group.skus || []),
    ...Object.keys(group.sizes || {}),
    ...Object.keys(group.colors || {}),
    ...(group.items || []).flatMap((item) => [
      item.client_name,
      item.sku,
      item.description,
      item.variant,
      item.size,
      item.status
    ])
  ];

  const searchMatch = !searchFilter || haystack.some((value) => includesSearch(value, searchFilter));

  return statusMatch && searchMatch;
};

export const filterGroups = (groups, filters = {}) => {
  const source = Array.isArray(groups) ? groups : [];

  return source
    .map((group) => {
      const subgroups = filterGroups(group.subgroups || [], filters);
      const selfMatch = groupMatchesFilters(group, filters);
      if (!selfMatch && !subgroups.length) return null;
      return {
        ...group,
        subgroups
      };
    })
    .filter(Boolean);
};

export const sortGroupsTree = (groups, sortBy = 'count', sortDir = 'desc') => {
  const source = Array.isArray(groups) ? groups : [];
  const compare = compareBy(sortBy, sortDir);

  return [...source]
    .map((group) => ({
      ...group,
      subgroups: sortGroupsTree(group.subgroups || [], sortBy, sortDir)
    }))
    .sort(compare);
};

export const paginate = (items, page = 1, pageSize = 25) => {
  const source = Array.isArray(items) ? items : [];
  const safePageSize = Number(pageSize) > 0 ? Number(pageSize) : 25;
  const total = source.length;
  const maxPage = Math.max(1, Math.ceil(total / safePageSize));
  const safePage = Math.min(Math.max(1, Number(page) || 1), maxPage);
  const start = (safePage - 1) * safePageSize;
  const end = start + safePageSize;
  return {
    page: safePage,
    pageSize: safePageSize,
    total,
    totalPages: maxPage,
    items: source.slice(start, end)
  };
};

export const flattenItems = (group) => {
  if (!group) return [];
  const localItems = Array.isArray(group.items) ? group.items : [];
  const subItems = (group.subgroups || []).flatMap(flattenItems);
  return [...localItems, ...subItems];
};

export const filterSortItems = (items, options = {}) => {
  const source = Array.isArray(items) ? items : [];
  const search = normalizeText(options.search);
  const sortBy = options.sortBy || 'quantity';
  const sortDir = options.sortDir || 'desc';

  const filtered = source.filter((item) => {
    if (!search) return true;
    return [
      item.client_name,
      item.sku,
      item.description,
      item.variant,
      item.size,
      item.status
    ].some((value) => includesSearch(value, search));
  });

  const compare = compareBy(sortBy, sortDir);
  return [...filtered].sort(compare);
};

export const collectTopGroups = (groups, limit = 8) => {
  return [...(groups || [])]
    .sort(compareBy('total_quantity', 'desc'))
    .slice(0, limit);
};

export const summarizeGroups = (groups) => {
  const nodes = Array.isArray(groups) ? groups : [];
  return nodes.reduce(
    (acc, group) => {
      acc.groupCount += 1;
      acc.orderCount += Number(group.count || 0);
      acc.totalQuantity += Number(group.total_quantity || 0);
      Object.entries(group.statuses || {}).forEach(([status, quantity]) => {
        acc.statuses[status] = (acc.statuses[status] || 0) + Number(quantity || 0);
      });

      if (group.subgroups?.length) {
        const child = summarizeGroups(group.subgroups);
        acc.groupCount += child.groupCount;
        acc.orderCount += child.orderCount;
        acc.totalQuantity += child.totalQuantity;
        Object.entries(child.statuses).forEach(([status, quantity]) => {
          acc.statuses[status] = (acc.statuses[status] || 0) + Number(quantity || 0);
        });
      }

      return acc;
    },
    { groupCount: 0, orderCount: 0, totalQuantity: 0, statuses: {} }
  );
};
